import { api } from '@/lib/api';
import { CONTRACTUAL_MANAGER_TAG } from './utils/contractual-managers';
import { getAccountByMongoOrganisationId } from '@/features/accounts/api';
import { mapV4AccountSensor, type V4AccountSensor } from './utils/sensor-mapping';
import type {
  Quote,
  QuotesFilters,
  QuotesResponse,
  QuoteStatusStats,
  QuoteStatsResult,
  Organisation,
  QuoteAccountFilterOption,
  QuoteAccountFilterPage,
  V3Quote,
  V3QuoteSku,
  V3QuoteSubtotal,
  CreateQuoteInput,
  CreateQuoteForNewCustomerInput,
  NewCustomerQuoteRow,
  UpdateQuoteInput,
  AddQuoteNoteInput,
  QuoteOperationsInternalNote,
  User,
  Pricelist,
  Subscription,
  InvoiceSummary,
  QuoteSensor,
  SensorSelectParams,
  SensorsResponse,
  AccountSensorFilterOptions,
  QuoteOperationsAuditLogsResponse,
} from './types';

// Matches a PostgreSQL UUID (v1–v5). Used to self-route id-bearing calls to
// the native V4 endpoint instead of the Mongo legacy path.
const PG_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// =============================================================================
// Quotes API Functions (V4 - PostgreSQL)
// Data source: PostgreSQL via GET /v4/quotes
// =============================================================================

/**
 * Get quotes with optional filtering, sorting, and pagination (V4)
 */
export async function getQuotes(filters: QuotesFilters = {}): Promise<QuotesResponse> {
  const queryParams: Record<string, string | number> = {
    pageNumber: filters.pageNumber ?? 0,
    pageLength: filters.pageLength ?? 25,
    sortKey: filters.sortKey ?? 'createdAt',
    sortDirection: filters.sortDirection ?? -1,
  };

  // Add search filter
  if (filters.search) {
    queryParams.search = filters.search;
  }

  // Add dedicated PO filter for Mode B server-side pagination
  if (filters.poNumber) {
    queryParams.poNumber = filters.poNumber;
  }

  // Dedicated client account-number filter (accounts.accountNumber only — the
  // # Compte column filter). Kept OUT of the global `search` so the server total
  // never counts coincidental matches in other fields.
  if (filters.accountNumber) {
    queryParams.accountNumber = filters.accountNumber;
  }

  // Add account filter
  if (filters.accountId) {
    queryParams.accountId = filters.accountId;
  }

  // Add status filter
  if (filters.status) {
    queryParams.status = filters.status;
  }

  // Add multiple status filter
  if (filters.status_in && filters.status_in.length > 0) {
    queryParams.status_in = JSON.stringify(filters.status_in);
  }

  // Add sales manager filter
  if (filters.salesManagerId) {
    queryParams.salesManagerId = filters.salesManagerId;
  }

  // Multi-select ID filters (from searchable dropdowns)
  if (filters.accountIdIn && filters.accountIdIn.length > 0) {
    queryParams.accountIdIn = JSON.stringify(filters.accountIdIn);
  }
  if (filters.salesManagerIdIn && filters.salesManagerIdIn.length > 0) {
    queryParams.salesManagerIdIn = JSON.stringify(filters.salesManagerIdIn);
  }

  return api.get<QuotesResponse>('/v4/quotes', queryParams);
}

/**
 * Get a single quote by ID (V4)
 */
export async function getQuote(quoteId: string): Promise<Quote> {
  return api.get<Quote>(`/v4/quotes/${quoteId}`);
}

/**
 * Get a single quote by quote number (V4)
 */
export async function getQuoteByNumber(quoteNumber: string): Promise<Quote> {
  return api.get<Quote>(`/v4/quotes/by-number/${encodeURIComponent(quoteNumber)}`);
}

/**
 * Check if a quote number already exists (V4)
 * Returns true if the quote number is taken, false if available.
 */
export async function checkQuoteNumberExists(quoteNumber: string): Promise<boolean> {
  const response = await api.get<{ exists: boolean }>(
    `/v4/quotes/by-number/${encodeURIComponent(quoteNumber)}`,
    { existsOnly: true },
  );
  return response.exists;
}

/**
 * Get a single quote by its MongoDB ObjectId (V4)
 */
export async function getQuoteByMongoId(mongoQuoteId: string): Promise<Quote> {
  return api.get<Quote>(`/v4/quotes/by-mongo-id/${encodeURIComponent(mongoQuoteId)}`);
}

export interface EnsureQuoteMongoResult {
  mongoQuoteId: string;
  created: boolean;
  relinked: boolean;
}

/**
 * Ensure a PostgreSQL quote has a live MongoDB quote backing document.
 * Used when a quote can be read from V4 but must still be edited/deleted through V3.
 */
export async function ensureQuoteMongo(quoteId: string): Promise<EnsureQuoteMongoResult> {
  return api.post<EnsureQuoteMongoResult>(`/v4/quotes/${quoteId}/ensure-mongo`);
}

/**
 * Get SKUs for a quote (fetched from MongoDB via V4 API)
 */
export interface QuoteSku {
  sku: string;
  name: string;
  quantity: number;
  unitPrice: number;
  description: string;
}

export async function getQuoteSkus(quoteId: string): Promise<QuoteSku[]> {
  const response = await api.get<{ skus: QuoteSku[] }>(`/v4/quotes/${quoteId}/skus`);
  return response.skus;
}

export async function getQuoteOperationsAuditLogs(
  quoteId: string,
  params: { pageNumber?: number; pageLength?: number } = {}
): Promise<QuoteOperationsAuditLogsResponse> {
  const queryParams: Record<string, number> = {};
  if (params.pageNumber !== undefined) queryParams.pageNumber = params.pageNumber;
  if (params.pageLength !== undefined) queryParams.pageLength = params.pageLength;

  return api.get<QuoteOperationsAuditLogsResponse>(
    `/v4/quotes/${quoteId}/operations-audit-logs`,
    queryParams
  );
}

/**
 * Get quote stats by status from the server-side aggregate endpoint.
 */
export async function getQuoteStats(filters: {
  accountId?: string;
  accountIdIn?: string[];
  salesManagerId?: string;
  status_in?: Quote['status'][];
  search?: string;
  poNumber?: string;
  accountNumber?: string;
} = {}): Promise<QuoteStatsResult> {
  const queryParams: Record<string, string> = {};

  if (filters.accountId) {
    queryParams.accountId = filters.accountId;
  }

  if (filters.accountIdIn && filters.accountIdIn.length > 0) {
    queryParams.accountIdIn = JSON.stringify(filters.accountIdIn);
  }

  if (filters.salesManagerId) {
    queryParams.salesManagerId = filters.salesManagerId;
  }

  if (filters.status_in && filters.status_in.length > 0) {
    queryParams.status_in = JSON.stringify(filters.status_in);
  }

  if (filters.search) {
    queryParams.search = filters.search;
  }

  if (filters.poNumber) {
    queryParams.poNumber = filters.poNumber;
  }

  if (filters.accountNumber) {
    queryParams.accountNumber = filters.accountNumber;
  }

  const stats = await api.get<Array<{ status: QuoteStatusStats['status']; count: number; amount: number }>>(
    '/v4/quotes/stats',
    queryParams,
  );

  const mappedStats = stats.map((stat) => ({
    status: stat.status,
    quantity: stat.count,
    amount: stat.amount,
  }));
  const total = mappedStats.find((stat) => stat.status === 'total')?.quantity || 0;

  return { stats: mappedStats, isApproximate: false, processed: total, total };
}

// =============================================================================
// Accounts API Functions (for customer filter)
// =============================================================================

interface V4AccountsListResponse {
  accounts: Array<{
    id: string;
    name: string;
    accountNumber: string | null;
    mongoOrganisationId?: string | null;
    salesManagerId?: string | null;
  }>;
  pageNumber?: number;
  pageLength?: number;
  total?: number;
}

function transformQuoteAccountFilterOption(
  account: { id: string; name: string; accountNumber: string | null },
): QuoteAccountFilterOption {
  return {
    _id: account.id,
    name: account.name,
    accountNumber: account.accountNumber,
  };
}

export async function searchQuoteAccountsPaginated(params: {
  search: string;
  pageNumber: number;
  pageLength: number;
}): Promise<QuoteAccountFilterPage> {
  const response = await api.get<V4AccountsListResponse>('/v4/accounts', {
    type: 'customer',
    search: params.search,
    pageLength: params.pageLength,
    pageNumber: params.pageNumber,
    sortKey: 'name',
    sortDirection: 1,
  });

  return {
    accounts: (response.accounts || []).map(transformQuoteAccountFilterOption),
    pageNumber: response.pageNumber ?? params.pageNumber,
    pageLength: response.pageLength ?? params.pageLength,
    total: response.total ?? response.accounts.length,
  };
}

export async function getQuoteAccountById(
  accountId: string,
): Promise<QuoteAccountFilterOption | null> {
  let response: {
    account?: { id: string; name: string; accountNumber: string | null };
  };

  try {
    response = await api.get<{
      account?: { id: string; name: string; accountNumber: string | null };
    }>(`/v4/accounts/${accountId}`);
  } catch {
    return null;
  }

  return response.account
    ? transformQuoteAccountFilterOption(response.account)
    : null;
}

/**
 * Get customer accounts for the dropdown filter.
 * Uses V4/PostgreSQL so IDs are UUIDs compatible with V4 quote filters.
 */
export async function getCustomerOrganisations(): Promise<Organisation[]> {
  const all: Organisation[] = [];
  let page = 0;
  const pageSize = 100;

  while (true) {
    const response = await api.get<V4AccountsListResponse>('/v4/accounts', {
      type: 'customer',
      pageLength: pageSize,
      pageNumber: page,
      sortKey: 'name',
      sortDirection: 1,
    });

    const accounts = response.accounts || [];
    all.push(
      ...accounts.map((a) => ({
        _id: a.id,
        name: a.name,
        mongoOrgId: a.mongoOrganisationId || undefined,
        salesManagerId: a.salesManagerId ?? null,
        // Carry the account number through as a string. Sub-accounts are
        // numbered `NNNN-NN` (71 of 240 customer accounts in prod), which the
        // numeric `org_num` below cannot represent — dropping it left 9
        // accounts named "Acces Pharma" rendering as identical bare names.
        accountNumber: a.accountNumber || undefined,
        // Legacy numeric organisation number; intentionally digits-only.
        org_num: a.accountNumber && /^\d+$/.test(a.accountNumber)
          ? Number.parseInt(a.accountNumber, 10)
          : undefined,
      }))
    );

    if (accounts.length < pageSize) break;
    page++;
  }

  return all;
}

// =============================================================================
// Legacy V3 Functions (MongoDB - for viewing quote HTML/PDF)
// =============================================================================

/**
 * Get quote HTML for viewing/printing (V3 - uses MongoDB)
 */
export async function getQuoteHtml(quoteId: string): Promise<string> {
  const response = await api.get<{ html: string }>(`/v4/legacy/quotes/${quoteId}/html`);
  return response.html;
}

// =============================================================================
// V3 Quote CRUD Functions (MongoDB - for mutations)
// =============================================================================

/**
 * Get a single quote by ID (V3 - full data for edit form)
 */
export async function getQuoteV3(quoteId: string): Promise<V3Quote> {
  const response = await api.get<{ quote: V3Quote }>(`/v4/legacy/quotes/${quoteId}`, {
    populate: JSON.stringify([
      'customer',
      'contract',
      'sales_manager',
      'contractual_manager',
      'billing_site',
      'shipping_addresses.site',
      'po_file',
    ]),
  });
  return response.quote;
}

/**
 * Map the form's V3QuoteSku rows → the V4 `QuoteLineItemInput` shape used by
 * both the create and update payloads. Single source of truth so create-save
 * and edit-save stay in lockstep (an edit that silently dropped these is what
 * "wiped" $/u, %, and quantity on existing quotes).
 */
function mapSkusToLineItems(
  skus: V3QuoteSku[],
  subtotals: V3QuoteSubtotal[] = [],
): Record<string, unknown>[] {
  return skus.map((sku, index) => {
    const subtotalMatch = subtotals.find((s) => s._id === sku.subtotal_id);
    const isNote = sku.sku === '__note__';
    return {
      sku: sku.sku ?? null,
      name: sku.name ?? null,
      description: sku.description ?? null,
      quantity: sku.quantity ?? 0,
      unitPrice: sku.unit_price ?? 0,
      discount: sku.discount ?? 0,
      total: isNote ? 0 : (sku.total ?? ((sku.quantity ?? 0) * (sku.unit_price ?? 0) * (1 - (sku.discount ?? 0) / 100))),
      sortOrder: index,
      itemNumber: null,
      subtotalGroup: sku.subtotal_id ?? null,
      subtotalLabel: subtotalMatch?.displayName ?? null,
      isNote,
    };
  });
}

/**
 * Translate a legacy snake_case `CreateQuoteInput` (the modal still emits
 * this shape) into the camelCase `CreateQuoteV4Input` the V4 backend
 * expects. Kept as a private helper so call sites stay unchanged during
 * the transition; the eventual modernization (rename QuoteFormModal
 * state to camelCase end-to-end) deletes this function entirely.
 */
function toCreateQuoteV4Payload(data: CreateQuoteInput & { accountId: string }): Record<string, unknown> {
  return {
    accountId: data.accountId,
    name: data.project_name ?? null,
    projectName: data.project_name ?? null,
    description: data.description ?? null,
    status: data.status,
    priority: data.priority ?? null,

    // Dates — all calendar-day strings, pass through unchanged.
    expirationDate: data.expiration_date ?? null,
    estimatedDelivery: data.estimated_delivery ?? null,
    acceptedDate: data.accepted_at ?? null,
    quoteDate: data.date ?? null,
    nextStepDeadline: data.next_step_deadline ?? null,

    // Financials
    total: data.total,
    currency: data.currency,
    taxCategory: data.tax_category,

    // Managers (Mongo ObjectIds preserved as varchar(24) on the PG row)
    salesManagerId: data.sales_manager ?? null,
    contractualManagerId: data.contractual_manager ?? null,

    // FKs / external links — prefer the PG UUID captured before the Mongo-id rewrite;
    // fall back to the legacy `subscription` field for call sites that don't set subscriptionId.
    subscriptionId: data.subscriptionId ?? data.subscription ?? null,
    hubspotLink: data.hubspot_link ?? null,
    gdriveLink: data.gdrive_link ?? null,

    // Other
    dealType: data.dealtype ?? null,
    quoteVersion: data.quote_version ?? null,
    probability: data.probability ?? null,
    language: data.language,
    paymentTerms: data.payment_terms ?? null,
    projectNumber: data.project_number ?? null,
    comment: data.comment ?? null,
    internalNotes: data.internal_notes ?? null,
    nextStep: data.next_step ?? null,
    trackingNumbers: data.tracking_numbers ?? null,
    pricelist: data.pricelist ?? null,
    ticket: data.ticket ?? null,

    // PO + addresses
    poNumber: data.po_number ?? null,
    billingSite: data.billing_site ?? null,
    billingAddress: data.billing_address ?? null,
    shippingAddress: data.shipping_addresses?.[0]?.address ?? null,
    shippingSite: data.shipping_addresses?.[0]?.site ?? null,

    // Line items — map V3QuoteSku → QuoteLineItemInput shape.
    lineItems: mapSkusToLineItems(data.skus ?? [], data.subtotals ?? []),
  };
}

/**
 * Reverse translator: V4-shape response → V3Quote shape that existing
 * callers (`QuotesTab.handleQuoteFormSubmit`, `useUpdateQuote` cache
 * patcher, etc.) still expect. Only the fields those callers read are
 * filled in; the rest are left undefined.
 */
function fromV4QuoteToV3Shape(v4: Record<string, unknown>): V3Quote {
  return {
    _id: (v4.mongoQuoteId as string) || (v4.id as string),
    // PG UUID, kept separate from `_id` (which becomes the Mongo shadow id when
    // the legacy dual-write bridge backs the quote). Callers routing follow-up
    // writes must use this so updates hit the guard-free V4 `PUT /v4/quotes/:id`
    // instead of the V3 legacy passthrough, which 423s on a migrated quote.
    id: (v4.id as string) ?? undefined,
    quote_number: v4.quoteNumber as string,
    status: v4.status as V3Quote['status'],
    customer: v4.accountId as string,
    project_name: (v4.projectName as string) ?? undefined,
    description: (v4.description as string) ?? undefined,
    date: (v4.quoteDate as string) ?? undefined,
    expiration_date: (v4.expirationDate as string) ?? undefined,
    estimated_delivery: (v4.estimatedDelivery as string) ?? undefined,
    accepted_at: (v4.acceptedDate as string) ?? undefined,
    sales_manager: (v4.salesManagerId as string) ?? undefined,
    contractual_manager: (v4.contractualManagerId as string) ?? undefined,
    total: typeof v4.total === 'string' ? parseFloat(v4.total) : (v4.total as number),
    currency: v4.currency as V3Quote['currency'],
    language: (v4.language as V3Quote['language']) ?? 'en',
    tax_category: (v4.taxCategory as V3Quote['tax_category']) ?? undefined,
    skus: Array.isArray(v4.lineItems) ? v4.lineItems.map((item: Record<string, unknown>) => ({
      sku: (item.sku as string) ?? '',
      name: (item.name as string) ?? '',
      description: (item.description as string) ?? '',
      quantity: typeof item.quantity === 'string' ? parseFloat(item.quantity) : (item.quantity as number),
      unit_price: typeof item.unitPrice === 'string' ? parseFloat(item.unitPrice) : (item.unitPrice as number),
      discount: typeof item.discount === 'string' ? parseFloat(item.discount) : (item.discount as number) || 0,
      total: typeof item.total === 'string' ? parseFloat(item.total) : (item.total as number),
      item_number: (item.itemNumber as number) ?? undefined,
      subtotal_id: (item.subtotalGroup as string) ?? undefined,
    })) : [],
    notes: [],
  };
}

/**
 * Create a new quote.
 *
 * V4-native: POSTs to `/v4/quotes`, which creates a PostgreSQL row and
 * NO MongoDB doc — the quote "stays in V2" per the policy that took
 * effect when `accounts_sync` was stopped. Callers must populate
 * `data.accountId` with the PG account UUID (the legacy `customer`
 * Mongo id alone is not enough for the V4 endpoint to resolve the FK).
 *
 * The response is translated back to the V3 `V3Quote` shape so that
 * existing consumers (`QuotesTab.handleQuoteFormSubmit`'s optimistic
 * injection, `useCreateQuote`'s downstream callers) keep reading
 * `created.project_name`, `created._id`, `created.skus[]`, etc.
 * unchanged. That translator will be removed when the modal + call
 * sites are modernized to camelCase end-to-end.
 */
export async function createQuote(data: CreateQuoteInput): Promise<V3Quote> {
  let accountId = data.accountId;
  // Fallback for call sites that don't have the PG account UUID handy
  // (QuotesPipelinePage, QuotesPage): resolve from the legacy `customer`
  // Mongo organisation id via the dedicated lookup endpoint. Callers that
  // do have accountId (QuotesTab.handleQuoteFormSubmit) skip the round-trip.
  if (!accountId && data.customer) {
    const result = await getAccountByMongoOrganisationId(data.customer);
    if (result.status === 'created') {
      accountId = result.id;
    } else {
      throw new Error(
        'Cannot create V4 quote: the customer account has not been materialized '
        + 'in PostgreSQL yet. Import or create the account before creating quotes against it.',
      );
    }
  }
  if (!accountId) {
    throw new Error('createQuote: either accountId or customer (Mongo org id) is required');
  }
  const payload = toCreateQuoteV4Payload({ ...data, accountId });
  const response = await api.post<{ quote: Record<string, unknown> }>('/v4/quotes', payload);
  const created = fromV4QuoteToV3Shape(response.quote);
  // `customer` in the V3 shape is the Mongo organisation id, not the PG
  // account UUID the V4 response carries; preserve the caller's Mongo org id
  // so downstream V3 consumers don't get a UUID where a Mongo id is expected.
  if (data.customer) created.customer = data.customer;
  return created;
}

/**
 * Create a header-only PG-native quote for a brand-new customer (V4/PostgreSQL).
 * The PG account is auto-created server-side behind
 * `FEATURE_AUTO_CREATE_ACCOUNT_ON_QUOTE`. No line items are sent. Returns the raw
 * PG quote row (see `NewCustomerQuoteRow` — deliberately not the full `Quote`
 * shape, which requires joined/enriched fields this endpoint doesn't return).
 */
export async function createQuoteForNewCustomer(
  input: CreateQuoteForNewCustomerInput,
): Promise<NewCustomerQuoteRow> {
  const response = await api.post<{ quote: NewCustomerQuoteRow }>('/v4/quotes', input);
  return response.quote;
}

/**
 * Translate a PARTIAL legacy snake_case `UpdateQuoteInput` into the
 * camelCase PUT `/v4/quotes/:id` body. Mirrors `toCreateQuoteV4Payload`'s
 * field mapping but emits ONLY keys that are present in `data` — the V4
 * update endpoint replaces only the columns it receives, so a sparse
 * payload is a true partial update.
 *
 * Line items ARE mapped, but ONLY when a non-empty `skus` set is present.
 * The V4 update endpoint replaces line items atomically (delete-then-insert)
 * whenever `lineItems` is present — even `[]` — so:
 *   - a non-empty array persists edits to $/u, %, quantity, and row add/remove
 *     (editing an existing quote's items is a core flow, and omitting them is
 *     exactly what silently "wiped" those edits on save);
 *   - an absent/empty `skus` is deliberately NOT emitted, so a status-only or
 *     field-only edit (or a form that never loaded items) can't wipe them.
 */
function toUpdateQuoteV4Payload(data: UpdateQuoteInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const has = (key: keyof UpdateQuoteInput) => Object.prototype.hasOwnProperty.call(data, key);

  // Identification / status
  if (has('project_name')) {
    payload.name = data.project_name ?? null;
    payload.projectName = data.project_name ?? null;
  }
  if (has('description')) payload.description = data.description ?? null;
  if (has('status')) payload.status = data.status;
  if (has('priority')) payload.priority = data.priority ?? null;

  // Dates — calendar-day strings, pass through unchanged.
  if (has('expiration_date')) payload.expirationDate = data.expiration_date ?? null;
  if (has('estimated_delivery')) payload.estimatedDelivery = data.estimated_delivery ?? null;
  if (has('accepted_at')) payload.acceptedDate = data.accepted_at ?? null;
  if (has('date')) payload.quoteDate = data.date ?? null;
  if (has('next_step_deadline')) payload.nextStepDeadline = data.next_step_deadline ?? null;

  // Financials
  if (has('total')) payload.total = data.total;
  if (has('currency')) payload.currency = data.currency;
  if (has('tax_category')) payload.taxCategory = data.tax_category;

  // Managers (Mongo ObjectIds preserved as varchar(24) on the PG row)
  if (has('sales_manager')) payload.salesManagerId = data.sales_manager ?? null;
  if (has('contractual_manager')) payload.contractualManagerId = data.contractual_manager ?? null;

  // FKs / external links
  if (has('subscription')) payload.subscriptionId = data.subscription ?? null;
  if (has('hubspot_link')) payload.hubspotLink = data.hubspot_link ?? null;
  if (has('gdrive_link')) payload.gdriveLink = data.gdrive_link ?? null;

  // Sales/quote metadata
  if (has('dealtype')) payload.dealType = data.dealtype ?? null;
  if (has('quote_version')) payload.quoteVersion = data.quote_version ?? null;
  if (has('probability')) payload.probability = data.probability ?? null;
  if (has('language')) payload.language = data.language;
  if (has('payment_terms')) payload.paymentTerms = data.payment_terms ?? null;
  if (has('project_number')) payload.projectNumber = data.project_number ?? null;
  if (has('comment')) payload.comment = data.comment ?? null;
  if (has('internal_notes')) payload.internalNotes = data.internal_notes ?? null;
  if (has('next_step')) payload.nextStep = data.next_step ?? null;
  if (has('tracking_numbers')) payload.trackingNumbers = data.tracking_numbers ?? null;
  if (has('pricelist')) payload.pricelist = data.pricelist ?? null;
  if (has('ticket')) payload.ticket = data.ticket ?? null;

  // PO + addresses
  if (has('po_number')) payload.poNumber = data.po_number ?? null;
  if (has('billing_site')) payload.billingSite = data.billing_site ?? null;
  if (has('billing_address')) payload.billingAddress = data.billing_address ?? null;
  if (has('shipping_addresses')) {
    payload.shippingAddress = data.shipping_addresses?.[0]?.address ?? null;
    payload.shippingSite = data.shipping_addresses?.[0]?.site ?? null;
  }

  // Line items — see the note above. Sent only when the form holds a non-empty
  // set, so real edits persist while field-only saves never wipe items.
  if (has('skus') && Array.isArray(data.skus) && data.skus.length > 0) {
    payload.lineItems = mapSkusToLineItems(data.skus, data.subtotals ?? []);
  }

  return payload;
}

/**
 * Update a quote.
 *
 * V4-native when `quoteId` is a PostgreSQL UUID: PUTs a PARTIAL snake→camel
 * payload to `/v4/quotes/:id` so V2's PG-backed read paths (Operations
 * Kanban, the quote Print/PDF rendered from PG, and the V4 lists) reflect
 * the change immediately. Line items are deliberately omitted from the
 * partial payload to avoid wiping them.
 *
 * Legacy fallback when `quoteId` is a 24-char Mongo ObjectId (a quote not
 * yet materialized in PG): keeps the V3 passthrough `/v4/legacy/quotes/:id`.
 *
 * The V4 response (`{ quote }`, camelCase) is translated back to the V3
 * `V3Quote` shape so existing consumers (cache patcher, form callers) keep
 * reading `_id`, `project_name`, `status`, etc. unchanged.
 */
export async function updateQuote(quoteId: string, data: UpdateQuoteInput): Promise<V3Quote> {
  if (PG_UUID_RE.test(quoteId)) {
    const payload = toUpdateQuoteV4Payload(data);
    const response = await api.put<{ quote?: Record<string, unknown> }>(`/v4/quotes/${quoteId}`, payload);
    if (!response.quote) {
      throw new Error('Updated quote payload missing from V4 response');
    }
    return fromV4QuoteToV3Shape(response.quote);
  }

  const response = await api.put<{ quote?: V3Quote }>(`/v4/legacy/quotes/${quoteId}`, data);
  if (!response.quote) {
    throw new Error('Updated quote payload missing from V3 response');
  }
  return response.quote;
}

/**
 * Delete a quote (soft delete).
 * Self-routes: PG UUID → DELETE /v4/quotes/:id (native PG soft-delete);
 * Mongo ObjectId → DELETE /v4/legacy/quotes/:id (legacy path).
 */
export async function deleteQuote(quoteId: string): Promise<void> {
  if (PG_UUID_RE.test(quoteId)) {
    await api.delete(`/v4/quotes/${quoteId}`);
    return;
  }
  await api.delete(`/v4/legacy/quotes/${quoteId}`);
}

/**
 * Discard a quote: POST /v4/quotes/:id/discard sets status=`cancelled` in PostgreSQL
 * (which drops it from the Operations Kanban) AND best-effort writes the same status
 * back to the Mongo quote, so the live V1/legacy quote readers reflect it (the
 * transitional PG→Mongo write-back of the PG-native cutover). The Kanban card and V4
 * list row both carry the PG UUID; assert it so a stray Mongo ObjectId fails fast here
 * rather than 500ing on the UUID `quotes.id` column.
 */
export async function discardQuote(pgQuoteId: string): Promise<void> {
  if (!PG_UUID_RE.test(pgQuoteId)) {
    throw new Error(`discardQuote requires a PostgreSQL quote id (UUID); got "${pgQuoteId}"`);
  }
  await api.post(`/v4/quotes/${pgQuoteId}/discard`);
}

/**
 * Add a note to a quote (V3 - uses MongoDB)
 */
export async function addQuoteNote(quoteId: string, data: AddQuoteNoteInput): Promise<V3Quote> {
  const response = await api.post<{ quote: V3Quote }>(`/v4/legacy/quotes/${quoteId}/notes`, data);
  return response.quote;
}

/**
 * Append an internal operations note to a quote (V4/PG operations_internal_notes,
 * append-only; author is stamped server-side from the session user).
 */
export async function addQuoteOperationsNote(
  quoteId: string,
  content: string,
): Promise<{ note: QuoteOperationsInternalNote; notes: QuoteOperationsInternalNote[] }> {
  return api.post(`/v4/quotes/${quoteId}/operations-internal-notes`, { content });
}

/**
 * Edit a note on a quote (V3 - uses MongoDB)
 */
export async function editQuoteNote(quoteId: string, noteId: string, content: string): Promise<void> {
  await api.put<string>(`/v4/legacy/quotes/${quoteId}/notes/${noteId}`, { editedContent: content });
}

/**
 * Delete a note from a quote (V3 - uses MongoDB)
 */
export async function deleteQuoteNote(quoteId: string, noteId: string): Promise<V3Quote> {
  const response = await api.delete<{ quote: V3Quote }>(`/v4/legacy/quotes/${quoteId}/notes/${noteId}`);
  return response.quote;
}

/**
 * Update the HubSpot deal a quote is already linked to (ATE-11441: there is no
 * create route - deal creation is human-only, so `hsId` is required).
 */
export async function updateHubspotDeal(quoteId: string, hsId: string): Promise<V3Quote> {
  const response = await api.put<{ quote: V3Quote }>(`/v4/legacy/quotes/${quoteId}/hubspotdeal/${hsId}`);
  return response.quote;
}

/**
 * Materialize a legacy MongoDB quote into PostgreSQL.
 *
 * Calls the V4 `POST /v4/quotes/from-mongo/:mongoQuoteId` endpoint (built
 * in PR #505) which is idempotent: a second call on an already-migrated
 * quote returns the existing PG row untouched. After success, the source
 * Mongo doc carries `migrated_to_pg: true` and writes against it are
 * rejected by the V3 PUT guard with 423 Locked — V4 becomes the only
 * write surface for that quote going forward.
 *
 * Response shape is V4-native (camelCase): `{ quote, materialized }`
 * where `quote` includes `id` (PG UUID), `mongoQuoteId`, `quoteNumber`,
 * `lineItems[]`, and the full set of V4 columns.
 */
export interface MaterializeQuoteResponse {
  /** PG row + embedded line items. Returned as an opaque object — callers
   *  should pull `id`, `mongoQuoteId`, and `quoteNumber` from it as needed. */
  quote: Record<string, unknown> & {
    id: string;
    mongoQuoteId: string;
    quoteNumber: string;
    lineItems: Record<string, unknown>[];
  };
  /** `true` if this call wrote to PG (new or re-sync), `false` if the
   *  fast-path returned an already-materialized row. */
  materialized: boolean;
}

export async function materializeQuoteFromMongo(
  mongoQuoteId: string,
): Promise<MaterializeQuoteResponse> {
  return api.post<MaterializeQuoteResponse>(
    `/v4/quotes/from-mongo/${encodeURIComponent(mongoQuoteId)}`,
  );
}

/**
 * Get quote HTML URL (for opening in new tab)
 */
export function getQuoteHtmlUrl(mongoQuoteId: string): string {
  return `/api/v4/legacy/quotes/${mongoQuoteId}/html`;
}

/**
 * Get quote work order URL
 */
export function getWorkOrderUrl(mongoQuoteId: string): string {
  return `/api/v4/legacy/quotes/${mongoQuoteId}/workorder`;
}

/**
 * Get quote packing slip URL
 */
export function getPackingSlipUrl(mongoQuoteId: string): string {
  return `/api/v4/legacy/quotes/${mongoQuoteId}/packingslip`;
}

// =============================================================================
// V4 Quote Rendering URLs (PostgreSQL - preferred)
// =============================================================================

/**
 * Get quote HTML URL via V4 endpoint (uses PostgreSQL UUID)
 */
export function getQuoteHtmlUrlV4(quoteId: string): string {
  return `/api/v4/quotes/${quoteId}/html`;
}

/**
 * Get quote work order URL via V4 endpoint (uses PostgreSQL UUID)
 */
export function getWorkOrderUrlV4(quoteId: string): string {
  return `/api/v4/quotes/${quoteId}/workorder`;
}

/**
 * Get quote packing slip URL via V4 endpoint (uses PostgreSQL UUID)
 */
export function getPackingSlipUrlV4(quoteId: string): string {
  return `/api/v4/quotes/${quoteId}/packingslip`;
}

// =============================================================================
// Users API Functions (for manager dropdowns)
// =============================================================================

interface V3UsersResponse {
  users: User[];
  pageNumber?: number;
  pageLength?: number;
  total?: number;
}

/**
 * Get sales users for dropdowns. V4 (PostgreSQL) only.
 * ATEK staff = employees + contractors. No Mongo organisation scope.
 */
export async function getSalesUsers(): Promise<User[]> {
  const response = await api.get<{
    users: Array<{ pg_id: string; firstname?: string; lastname?: string; email?: string }>;
    total: number;
  }>('/v4/users', {
    pageLength: 100,
    sortKey: 'firstname',
    sortDirection: 1,
    employment_type: 'employee,contractor',
    enabled: 'true',
    is_subaccount: 'false',
  });
  return (response.users || []).map((u) => ({
    _id: u.pg_id,
    firstname: u.firstname,
    lastname: u.lastname,
    email: u.email,
  })) as User[];
}

/**
 * Get users belonging to a specific organisation
 */
export async function getOrganisationUsers(orgId: string, tags?: string[], search?: string): Promise<User[]> {
  const params: Record<string, unknown> = {
    organisation: orgId,
    pageLength: 100,
    sortKey: 'firstname',
    sortDirection: 1,
  };
  if (tags?.length) {
    params.tags_in = tags;
  }
  // Server-side free-text search (firstname/lastname/email/username/...). Needed
  // because the response is capped at pageLength 100: filtering client-side only
  // would silently hide contacts on large accounts.
  if (search?.trim()) {
    params.search = search.trim();
  }
  const response = await api.get<V3UsersResponse>('/v4/legacy/users/', params);
  return response.users || [];
}

/**
 * Tag an existing organisation contact as Contractual Manager.
 *
 * Additive and organisation-scoped: it appends to organisation_data[].tags for
 * this organisation only, so the contact keeps any other role (IT Manager,
 * Technical Manager...) and its roles in other organisations are untouched.
 * Idempotent server-side when the tag is already present.
 */
export async function addContractualManagerTag(userId: string, orgId: string): Promise<void> {
  await api.post(`/v4/legacy/users/${userId}/organisation_data/${orgId}/tags`, {
    tags: [CONTRACTUAL_MANAGER_TAG],
  });
}

/**
 * Get quote changelog
 */
export async function getQuoteChangelog(quoteId: string): Promise<unknown[]> {
  const response = await api.get<{ changelog: unknown[] }>(`/v4/legacy/quotes/${quoteId}/changelog`);
  return response.changelog || [];
}

// =============================================================================
// Supporting API Functions (for form dropdowns)
// =============================================================================

/** Shape consumed by the dropdown and fillFromContract */
export interface SubscriptionDropdownItem {
  _id: string;          // PG UUID (used as dropdown value and stored in formData.contract)
  mongoContractId?: string | null; // MongoDB ObjectId (sent to MongoDB when creating quotes)
  name: string;
  contract_number?: string;
  customer?: string;
  pricelists?: string[];
}

/** @deprecated Use SubscriptionDropdownItem instead */
export type ContractDropdownItem = SubscriptionDropdownItem;

interface V4SubscriptionsListResponse {
  subscriptions: Array<{
    id: string;
    subscriptionNumber: string;
    name: string;
    status: string;
    mongoContractId?: string | null;
    salesManagerId?: string | null;
    contractualManagerIds?: string[];
    paymentTerms?: string | null;
    currency?: string | null;
    accountId: string;
  }>;
  total: number;
}

/**
 * Get contracts filtered by account.
 * Accepts either a PG accountId (UUID) or a MongoDB customerId (ObjectId string).
 * When pgAccountId is provided, it filters server-side via the V4 endpoint.
 */
export async function getSubscriptionsByCustomerForQuote(_customerId: string, pgAccountId?: string): Promise<SubscriptionDropdownItem[]> {
  const response = await api.get<V4SubscriptionsListResponse>('/v4/subscriptions', {
    pageLength: 100,
    sortKey: 'name',
    sortDirection: 1,
    ...(pgAccountId ? { accountId: pgAccountId } : {}),
  });
  return (response.subscriptions || []).map((c) => ({
    _id: c.id,
    mongoContractId: c.mongoContractId,
    name: c.name,
    contract_number: c.subscriptionNumber,
  }));
}

/** @deprecated Use getSubscriptionsByCustomerForQuote instead */
export const getContractsByCustomer = getSubscriptionsByCustomerForQuote;

interface V4SubscriptionDetailResponse {
  subscription: {
    id: string;
    name: string;
    subscriptionNumber?: string;
    mongoContractId?: string | null;
    salesManagerId?: string | null;
    contractualManagerIds?: string[];
    paymentTerms?: string | null;
    currency?: string | null;
    language?: string | null;
    accountId: string;
  };
}

/** Subscription detail shape consumed by fillFromContract */
export interface SubscriptionDetail {
  _id: string;
  mongoContractId?: string | null;
  name: string;
  contract_number?: string;
  sales_manager?: string;
  contractual_managers?: string[];
  payment_terms?: string;
  language?: string;
  currency?: string;
}

/** @deprecated Use SubscriptionDetail instead */
export type ContractDetail = SubscriptionDetail;

/**
 * Get a single subscription with full details (V4 PostgreSQL)
 */
export async function getSubscriptionDetail(subscriptionId: string): Promise<SubscriptionDetail | null> {
  try {
    const response = await api.get<V4SubscriptionDetailResponse>(`/v4/subscriptions/${subscriptionId}`);
    const c = response.subscription;
    if (!c) return null;
    return {
      _id: c.id,
      mongoContractId: c.mongoContractId,
      name: c.name,
      contract_number: c.subscriptionNumber,
      sales_manager: c.salesManagerId || undefined,
      contractual_managers: c.contractualManagerIds || [],
      payment_terms: c.paymentTerms || undefined,
      language: c.language || undefined,
      currency: c.currency || undefined,
    };
  } catch {
    return null;
  }
}

/** @deprecated Use getSubscriptionDetail instead */
export const getContractDetail = getSubscriptionDetail;

interface V3SitesResponse {
  sites: Array<{
    _id: string;
    name: string;
    address?: string;
    city?: string;
    province?: string;
    postal_code?: string;
    country?: string;
    customer?: string;
    tags?: string[];
    email?: string;
  }>;
  total?: number;
}

/**
 * Site shape returned by `GET /v4/sites` (PostgreSQL projection, kept in sync
 * from Mongo by the inline sites sync). `mongoSiteId` is the Mongo ObjectId —
 * the id quotes' `billing_site`/`shipping_site` columns store (varchar(24)) —
 * so the quote form MUST key on it, never on `id` (a PG uuid).
 */
export interface V4SiteListItem {
  id: string;
  mongoSiteId: string;
  name: string;
  addressLine1?: string | null;
  city?: string | null;
  stateProvince?: string | null;
  postalCode?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  tags?: string[] | null;
  disabled?: boolean;
}

interface V4SitesListResponse {
  sites: V4SiteListItem[];
  pageNumber?: number;
  pageLength?: number;
  total?: number;
}

/**
 * Get a customer's sites through the V4 sites API (account-scoped).
 */
export async function getSitesForAccount(pgAccountId: string): Promise<V4SiteListItem[]> {
  // Page through: /v4/sites rejects pageLength > 100, and an account can hold
  // far more than that (973 for one real customer), so a single request would
  // silently hide addresses from the picker — the #1234 failure all over again.
  // Same shape as getCustomerOrganisations above, plus a hard stop so a server
  // that keeps returning full pages cannot spin the browser.
  const all: V4SiteListItem[] = [];
  const pageSize = 100;
  const maxPages = 20; // 2000 sites, well past the largest real account

  for (let pageNumber = 0; pageNumber < maxPages; pageNumber += 1) {
    // eslint-disable-next-line no-await-in-loop
    const response = await api.get<V4SitesListResponse>('/v4/sites', {
      accountId: pgAccountId,
      pageLength: pageSize,
      pageNumber,
      sortKey: 'name',
      sortDirection: 1,
    });
    const page = response.sites || [];
    all.push(...page);
    if (page.length < pageSize) break;
  }

  return all;
}

/**
 * Get sites filtered by customer
 */
export async function getSitesByCustomer(customerId: string): Promise<V3SitesResponse['sites']> {
  const response = await api.get<V3SitesResponse>('/v4/legacy/sites/', {
    organisation: customerId,
    deleted: 'false',
    pageLength: 100,
    sortKey: 'name',
    sortDirection: 1,
  });
  return response.sites || [];
}

interface V3TicketsResponse {
  tickets: Array<{
    _id: string;
    name: string;
    ticket_number?: string | number;
    organisation?: string;
  }>;
  total?: number;
}

/**
 * SKU catalog item
 */
export interface SkuCatalogItem {
  _id: string;
  sku: string;
  name: string | { fr: string; en: string };
  name_fr?: string;
  description_en?: string;
  description_fr?: string;
  value?: number;
  cost?: number;
  category?: string;
  type?: 'inventory' | 'non_inventory' | 'service' | string;
  saleable?: boolean;
  activated?: boolean;
}

interface V3SkusResponse {
  skus: SkuCatalogItem[];
  total?: number;
}

function parseCatalogMoney(value: string | number | null | undefined): number | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Get SKU catalog (saleable, activated items)
 */
export async function getSkuCatalog(): Promise<SkuCatalogItem[]> {
  const response = await api.get<V3SkusResponse>('/v4/legacy/skus/', {
    saleable: 'true',
    activated: 'true',
    deleted: 'false',
    pageLength: 100,
    sortKey: 'sku',
    sortDirection: 1,
  });
  return response.skus || [];
}

/**
 * Search SKU catalog with pagination.
 * Backed by V4/Postgres and used by the quote SKU combobox.
 */
export async function searchSkuCatalogPaginated({
  search,
  pageNumber,
  pageLength,
}: {
  search: string;
  pageNumber: number;
  pageLength: number;
}): Promise<{
  skus: SkuCatalogItem[];
  pageNumber: number;
  pageLength: number;
  total: number;
}> {
  const response = await api.get<{
    skus: Array<{
      id: string;
      mongoId?: string | null;
      sku: string;
      type?: string | null;
      nameFr: string;
      nameEn: string;
      descriptionFr?: string | null;
      descriptionEn?: string | null;
      value?: string | number | null;
      cost?: string | number | null;
      saleable?: boolean;
      activated?: boolean;
    }>;
    pageNumber: number;
    pageLength: number;
    total: number;
  }>('/v4/wms/skus', {
    search,
    pageNumber,
    pageLength,
    activated: true,
    saleable: true,
    sortKey: 'sku',
    sortDirection: 1,
  });

  return {
    skus: (response.skus || []).map((sku) => ({
      _id: sku.mongoId || '',
      sku: sku.sku,
      type: sku.type || undefined,
      name: {
        fr: sku.nameFr,
        en: sku.nameEn,
      },
      name_fr: sku.nameFr,
      description_en: sku.descriptionEn || undefined,
      description_fr: sku.descriptionFr || undefined,
      value: parseCatalogMoney(sku.value),
      cost: parseCatalogMoney(sku.cost),
      saleable: sku.saleable,
      activated: sku.activated,
    })),
    pageNumber: response.pageNumber ?? pageNumber,
    pageLength: response.pageLength ?? pageLength,
    total: response.total ?? 0,
  };
}

// =============================================================================
// Pricelists API Functions
// =============================================================================

interface V3PricelistsResponse {
  pricelists: Pricelist[];
  total?: number;
}

export async function getPricelists(): Promise<Pricelist[]> {
  const response = await api.get<V3PricelistsResponse>('/v4/pricelists', {
    deleted: 'false',
    sortKey: 'name',
    sortDirection: 1,
    pageLength: 100,
  });
  return response.pricelists || [];
}

// =============================================================================
// Subscriptions API Functions
// =============================================================================

interface V3SubscriptionsResponse {
  subscriptions: Subscription[];
  total?: number;
}

export async function getSubscriptionsByCustomer(customerId: string): Promise<Subscription[]> {
  const response = await api.get<V3SubscriptionsResponse>('/v4/legacy/subscriptions', {
    customer: customerId,
    deleted: 'false',
    sortKey: 'name',
    sortDirection: 1,
    pageLength: 100,
  });
  return response.subscriptions || [];
}

// =============================================================================
// Invoices API Functions (for quote invoice tracking)
// =============================================================================

interface V3InvoicesResponse {
  invoices: InvoiceSummary[];
  total?: number;
}

export async function getInvoicesByQuoteNumber(quoteNumber: string): Promise<InvoiceSummary[]> {
  const allInvoices: InvoiceSummary[] = [];
  let page = 0;

  while (true) {
    const response = await api.get<V3InvoicesResponse>('/v4/legacy/invoices', {
      quote_number_eq: quoteNumber,
      pageNumber: page,
      pageLength: 100,
    });

    const invoices = response.invoices || [];
    allInvoices.push(...invoices);

    if (invoices.length < 100) break;
    page++;
  }

  return allInvoices;
}

/**
 * Upload a PO file to a quote.
 *
 * Two steps: store the file through the legacy files proxy (returns the bare
 * file id), then persist it on the quote via the regular legacy update — its
 * allowlist already carries `po_file`. The old single POST to
 * `/v4/legacy/quotes/:id/po-file` targeted a route no backend ever exposed.
 */
export async function uploadPoFile(quoteId: string, file: File): Promise<V3Quote> {
  const formData = new FormData();
  formData.append('file', file);
  const fileId = await api.upload<string>('/v4/legacy/v2/files/upload/', formData);
  return updateQuote(quoteId, { po_file: fileId });
}

/**
 * Delete the PO file from a quote by clearing `po_file` on the legacy update.
 */
export async function deletePoFile(quoteId: string): Promise<V3Quote> {
  return updateQuote(quoteId, { po_file: null });
}

/**
 * Get distinct project numbers from existing quotes (for combobox suggestions)
 */
export async function getDistinctProjectNumbers(): Promise<string[]> {
  const seen = new Set<string>();
  let page = 0;
  const maxPages = 5;

  while (page < maxPages) {
    const response = await api.get<QuotesResponse>('/v4/quotes', {
      pageNumber: page,
      pageLength: 100,
      sortKey: 'projectNumber',
      sortDirection: 1,
    });
    for (const q of response.quotes || []) {
      if (q.projectNumber) seen.add(q.projectNumber);
    }
    if ((response.quotes || []).length < 100) break;
    page++;
  }

  return Array.from(seen).sort();
}

export async function getNextQuoteNumber(): Promise<string> {
  // Use dedicated V4 endpoint that queries both PostgreSQL and MongoDB globally
  // (no organisation scoping) to find the true max quote number and prevent collisions.
  const response = await api.get<{ nextQuoteNumber: string }>('/v4/quotes/next-number');
  return response.nextQuoteNumber || '0001';
}

export async function getTicketsByCustomer(customerId: string): Promise<V3TicketsResponse['tickets']> {
  const tickets: V3TicketsResponse['tickets'] = [];
  const pageLength = 500;
  let pageNumber = 0;

  while (true) {
    const response = await api.get<V3TicketsResponse>('/v4/legacy/tickets/', {
      organisation: customerId,
      pageLength,
      pageNumber,
      sortKey: 'ticket_number',
      sortDirection: -1,
    });

    const page = response.tickets || [];
    tickets.push(...page.map((ticket) => ({
      _id: ticket._id,
      name: ticket.name,
      ticket_number: ticket.ticket_number != null ? String(ticket.ticket_number) : undefined,
      organisation: ticket.organisation,
    })));

    if (page.length < pageLength) break;
    pageNumber += 1;
  }

  return tickets;
}

// =============================================================================
// Sensor API Functions (V3/V2 - MongoDB)
// =============================================================================

const SENSOR_POPULATE = JSON.stringify(['stream', 'installationqualification', 'location']);

function asQuoteSensorArray(value: unknown): QuoteSensor[] {
  return Array.isArray(value) ? value : [];
}

export async function getSensorsByQuote(quoteNumber: string): Promise<QuoteSensor[]> {
  const response = await api.get<{
    sensors: Array<{
      id: string;
      mongoSensorId: string;
      name: string;
      status: number;
      customId: string | null;
      streamName: string | null;
      site: { id: string; name: string } | null;
      location: { id: string; name: string } | null;
      invoices: string[] | null;
      quotes: string[] | null;
    }>;
  }>(`/v4/sensors/by-quote/${quoteNumber}`);

  return (response.sensors || []).map((s) => ({
    _id: s.mongoSensorId,
    customid: s.customId || undefined,
    name: s.name,
    status: s.status,
    location: s.location ? { _id: s.location.id, name: s.location.name } : undefined,
    stream: s.streamName ? { _id: '', name: s.streamName } : undefined,
    invoices: s.invoices || [],
    quotes: s.quotes || [],
  }));
}

export async function getSensorsByInvoices(
  invoiceNumbers: string[],
  organisation?: string,
): Promise<QuoteSensor[]> {
  if (!invoiceNumbers.length) return [];
  // NEVER send organisation:'all' — the V3 sensors DAO matches it literally
  // ({organisation: "all"} vs an ObjectId field) and the query returns nothing.
  // Scope to the real organisation when the caller knows it, otherwise omit.
  const response = await api.get<{ sensors: QuoteSensor[] }>('/v4/legacy/sensors', {
    invoices_in: JSON.stringify(invoiceNumbers),
    archived: 'false',
    deleted: 'false',
    populate: SENSOR_POPULATE,
    ...(organisation ? { organisation } : {}),
    pageLength: 100,
  });
  return asQuoteSensorArray(response.sensors);
}

export async function getSelectableSensors(
  customerId: string,
  params: SensorSelectParams = {}
): Promise<SensorsResponse> {
  const filterQuery = {
    organisation: customerId,
    deleted: 'false',
    archived: 'false',
    ...(params.search ? { search: params.search } : {}),
    ...(params.nameSearch ? { name: params.nameSearch } : {}),
    ...(params.streamSearch ? { stream_name: params.streamSearch } : {}),
    ...(params.locationSearch ? { location: params.locationSearch } : {}),
    ...(params.status_in?.length ? { status_in: JSON.stringify(params.status_in) } : {}),
  };
  // V3 list responses carry no total; fetch it separately (count: true returns { sensors: <n> })
  const [response, countResponse] = await Promise.all([
    api.get<{ sensors: QuoteSensor[]; total?: number }>('/v4/legacy/sensors', {
      ...filterQuery,
      populate: SENSOR_POPULATE,
      pageNumber: params.pageNumber ?? 0,
      pageLength: params.pageLength ?? 25,
      sortKey: params.sortKey ?? 'name',
      sortDirection: params.sortDirection ?? 1,
    }),
    api.get<{ sensors: number }>('/v4/legacy/sensors', { ...filterQuery, count: 'true' })
      .catch(() => null),
  ]);
  const sensors = asQuoteSensorArray(response.sensors);
  const total = typeof countResponse?.sensors === 'number'
    ? countResponse.sensors
    : (response.total || sensors.length);
  return { data: sensors, total };
}

/**
 * Fetch sensors by PG account ID (V4 fallback when V3 org-based query returns nothing).
 * Maps V4 response to QuoteSensor shape (calibration/installation fields will be empty).
 */
export async function getSelectableSensorsByAccount(
  accountId: string,
  params: SensorSelectParams = {}
): Promise<SensorsResponse> {
  const queryParams: Record<string, string | number> = {
    pageNumber: params.pageNumber ?? 0,
    pageLength: params.pageLength ?? 25,
    sortKey: params.sortKey ?? 'name',
    sortDirection: params.sortDirection ?? 1,
  };
  if (params.search) queryParams.search = params.search;
  if (params.nameSearch) queryParams.nameSearch = params.nameSearch;
  if (params.streamSearch) queryParams.streamSearch = params.streamSearch;
  if (params.locationSearch) queryParams.siteLocationSearch = params.locationSearch;
  if (params.status_in?.length) queryParams.status = params.status_in.join(',');
  if (params.usergroupId) queryParams.usergroupId = params.usergroupId;
  if (params.sensorgroupId) queryParams.sensorgroupId = params.sensorgroupId;
  if (params.siteId) queryParams.siteId = params.siteId;

  const response = await api.get<{ sensors: V4AccountSensor[]; total: number }>(
    `/v4/accounts/${accountId}/sensors`,
    queryParams,
  );
  const sensors = (response.sensors || []).map(mapV4AccountSensor);
  return { data: sensors, total: response.total || sensors.length };
}

/**
 * Distinct usergroups / sensorgroups / sites present on the account's candidate
 * sensors — the option lists for the sensor-select dialog's structured filters
 * (#1177). Same population as getSelectableSensorsByAccount's default view.
 */
export async function getSensorFilterOptions(
  accountId: string
): Promise<AccountSensorFilterOptions> {
  const response = await api.get<AccountSensorFilterOptions>(
    `/v4/accounts/${accountId}/sensor-filter-options`
  );
  return {
    usergroups: response.usergroups || [],
    sensorgroups: response.sensorgroups || [],
    sites: response.sites || [],
  };
}

/**
 * Fetch sensors by PG account ID (returns QuoteSensor[] for simpler usage)
 */
export async function getSensorsByAccount(accountId: string): Promise<QuoteSensor[]> {
  const PAGE_SIZE = 100;
  const first = await getSelectableSensorsByAccount(accountId, { pageLength: PAGE_SIZE, pageNumber: 0 });
  const all = [...first.data];

  const totalPages = Math.ceil(first.total / PAGE_SIZE);
  if (totalPages > 1) {
    const remaining = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        getSelectableSensorsByAccount(accountId, { pageLength: PAGE_SIZE, pageNumber: i + 1 }),
      ),
    );
    for (const page of remaining) all.push(...page.data);
  }

  return all;
}

export interface QuoteTransmitter {
  id: string;
  mongoTransmitterId: string;
  devId: string;
  name: string | null;
  activated: boolean;
  site: { id: string; name: string } | null;
  location: { id: string; name: string } | null;
  quoteNumber: string;
  configuredAt: string | null;
}

export async function getTransmittersByQuote(quoteNumber: string): Promise<QuoteTransmitter[]> {
  const response = await api.get<{
    transmitters: Array<{
      id: string;
      mongoTransmitterId: string;
      devId: string;
      name: string | null;
      activated: boolean;
      siteId: string | null;
      siteName: string | null;
      locationId: string | null;
      locationName: string | null;
      quoteNumber: string;
      configuredAt: string | null;
    }>;
  }>(`/v4/sensors/transmitters-by-quote/${quoteNumber}`);

  return (response.transmitters || []).map((t) => ({
    id: t.id,
    mongoTransmitterId: t.mongoTransmitterId,
    devId: t.devId,
    name: t.name,
    activated: t.activated,
    site: t.siteId ? { id: t.siteId, name: t.siteName || '' } : null,
    location: t.locationId ? { id: t.locationId, name: t.locationName || '' } : null,
    quoteNumber: t.quoteNumber,
    configuredAt: t.configuredAt,
  }));
}

export async function addSensorToQuote(sensorId: string, quoteNumber: string): Promise<void> {
  await api.post(`/v4/sensors/${sensorId}/add-quote`, { quoteNumber });
}

export async function removeSensorFromQuote(sensorId: string, quoteNumber: string): Promise<void> {
  await api.post(`/v4/sensors/${sensorId}/remove-quote`, { quoteNumber });
}
